import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/pro-light-svg-icons/faCopy';
import { faEnvelope } from '@fortawesome/pro-light-svg-icons/faEnvelope';
import type { MailboxProviderId } from 'fym-shared/src/music24/outreach/mailboxProviders';
import { TypedMemo } from '../../../common/types';
import { LoadingView } from '../../../common/views/LoadingView';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { useMailboxAdminMessage } from '../hooks/useMailboxAdminMessage';
import { GuideHead } from './GuideHead';
import { mailboxGuideMessages as messages } from './mailboxGuideMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the guide; each part uses its own slice of it */
import styles from './MailboxGuide.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** A message for the mail admin of a work mailbox (design outreach-v2 o5): copy it or open it in email. */
const AdminMessageComponent: React.FC<{ email: string; providerId: MailboxProviderId }> = ({ email, providerId }) => {
  const intl = useIntl();
  const { message, isLoading } = useMailboxAdminMessage(email, providerId);
  const [copied, setCopied] = useState(false);

  return (
    <>
      <GuideHead
        step={2}
        title={intl.formatMessage(messages.adminTitle)}
        intro={intl.formatMessage(messages.adminIntro)}
      />
      <div className={styles.body}>
        {isLoading && <LoadingView />}
        {message !== null && (
          <>
            <div className={styles.message} data-testid="admin-message">
              {message.text}
            </div>
            <div className="d-flex flex-wrap gap-2">
              <SMLButton
                size="sm"
                color="primary"
                omitCap
                leftIcon={faCopy}
                onClick={() => {
                  navigator.clipboard
                    .writeText(message.text)
                    .then(() => setCopied(true))
                    .catch(console.error);
                }}
              >
                {intl.formatMessage(copied ? messages.adminCopied : messages.adminCopy)}
              </SMLButton>
              <a
                className="btn btn-sm btn-outline-secondary"
                href={`mailto:?subject=${encodeURIComponent(message.subject)}&body=${encodeURIComponent(message.text)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                {intl.formatMessage(messages.adminOpenMail)}
              </a>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export const AdminMessage = TypedMemo(AdminMessageComponent);
